import { createContext, useCallback, useContext, useId, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, X } from 'lucide-react';
import { Sheet } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { getRegistryEntry, type RecordRef, type RegistryEntry } from './registry';

interface OpenOptions {
  /** Element that gets focus when the viewer closes (the opening link). */
  returnFocusTo?: HTMLElement | null;
}

interface ViewerApi {
  open: (ref: RecordRef, opts?: OpenOptions) => void;
}

const ViewerContext = createContext<ViewerApi>({ open: () => undefined });

export function useRecordViewer(): ViewerApi {
  return useContext(ViewerContext);
}

/**
 * One viewer for the signed-in layout. `open` from outside the viewer resets the history; from
 * inside (RecordLinks rendered in the sheet get the inside api) it adds a step. Back goes one
 * step, Close clears. One sheet only, never a sheet over a sheet.
 */
export function RecordViewerProvider({ children }: { children: ReactNode }) {
  const [history, setHistory] = useState<RecordRef[]>([]);
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null);

  const outsideApi = useMemo<ViewerApi>(
    () => ({
      open: (ref, opts) => {
        setHistory([ref]);
        setReturnFocusTo(opts?.returnFocusTo ?? null);
      },
    }),
    [],
  );
  const insideApi = useMemo<ViewerApi>(() => ({ open: (ref) => setHistory((h) => [...h, ref]) }), []);

  const current = history[history.length - 1];
  const close = useCallback(() => setHistory([]), []);
  const back = useCallback(() => setHistory((h) => h.slice(0, -1)), []);

  return (
    <ViewerContext.Provider value={outsideApi}>
      {children}
      <ViewerSheet
        current={current}
        canGoBack={history.length > 1}
        onBack={back}
        onClose={close}
        returnFocusTo={returnFocusTo}
        insideApi={insideApi}
      />
    </ViewerContext.Provider>
  );
}

function ViewerSheet({
  current, canGoBack, onBack, onClose, returnFocusTo, insideApi,
}: {
  current: RecordRef | undefined;
  canGoBack: boolean;
  onBack: () => void;
  onClose: () => void;
  returnFocusTo: HTMLElement | null;
  insideApi: ViewerApi;
}) {
  const titleId = useId();
  const entry = current ? getRegistryEntry(current.type) : null;

  return (
    <Sheet open={!!current} onClose={onClose} labelledBy={titleId} returnFocusTo={returnFocusTo} testId="record-viewer">
      <ViewerContext.Provider value={insideApi}>
        {current && entry && (
          <RecordContent
            key={`${current.type}:${current.id}`}
            current={current}
            entry={entry}
            titleId={titleId}
            canGoBack={canGoBack}
            onBack={onBack}
            onClose={onClose}
          />
        )}
      </ViewerContext.Provider>
    </Sheet>
  );
}

function RecordContent({
  current, entry, titleId, canGoBack, onBack, onClose,
}: {
  current: RecordRef;
  entry: RegistryEntry<unknown>;
  titleId: string;
  canGoBack: boolean;
  onBack: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('records');
  const { t: tc, i18n } = useTranslation('common');
  const { data, isLoading, isError, refetch } = entry.useData(current.id);
  const status = data !== undefined && entry.status ? entry.status(data) : null;
  // POS-I249: the header badge reads the translated status when the common namespace has one.
  const statusLabel = (st: string) => {
    const key = `status_${st.toLowerCase()}`;
    return i18n.exists(key, { ns: 'common' }) ? tc(key) : undefined;
  };
  const Body = entry.Body as ComponentType<{ data: unknown }>;

  return (
    <>
      <header className="flex items-start gap-2 border-b px-4 py-3">
        {canGoBack && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="record-viewer-back"
            onClick={onBack}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            <span className="ms-1">{t('back')}</span>
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{t(entry.labelKey)}</p>
          <h2 id={titleId} className="flex flex-wrap items-center gap-2 text-base font-semibold">
            <bdi dir="ltr" className="break-all font-mono">{current.label}</bdi>
            {status && <StatusBadge status={status} label={statusLabel(status)} />}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          data-sheet-initial=""
          data-testid="record-viewer-close"
          onClick={onClose}
          aria-label={t('close')}
          className="shrink-0"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading && (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {isError && (
          <div role="alert" className="space-y-3 text-sm">
            <p className="text-muted-foreground">{t('notAvailable')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('retry')}
            </Button>
          </div>
        )}
        {data !== undefined && !isError && <Body data={data} />}
      </div>
    </>
  );
}
